import assetUtils from 'app-lib/assetUtils'
import { abbreviate, autoFormat, formatPercent } from 'app-lib/numberUtils'
import { chainConfigs, links } from 'bzx-common/src/config/appConfig'
import { InputAmount } from 'bzx-common/src/shared-components/InputAmount/InputAmount'
import { observer } from 'mobx-react'
import React from 'react'
import { Link } from 'react-router-dom'
import ExternalLink from 'shared-components/ExternalLink'
import { Button, ButtonClose, Icon, InfoTip } from 'ui-framework'
import LendVM from '../LendVM'
import WrapNativeForm from './WrapNativeForm'

const noop = () => {
  // TODO: either complete or remove itoken price update
  console.log('noop')
}

export function LendForm({ vm }: { vm: LendVM }) {
  const { chainId } = vm.rootStore.web3Connection
  if (!vm.assetSelected || !vm.lendForm.assetSelectedInfo || !chainId) {
    return null
  }
  const { lendForm } = vm
  const { isLending, nextSupplyRate } = lendForm
  const { lendItem, profitEstimates } = vm.lendForm.assetSelectedInfo
  const { displayName, spriteLogo } = assetUtils.getCommon(lendForm.inputLendAsset)
  const chain = chainConfigs[chainId]

  /** If interest rate is very low, we display 0 but it's a bit more, so we prefix with '~' */
  const numPrefix =
    !lendForm.lendAmountAsBN.isNaN() &&
    !lendForm.lendAmountAsBN.isZero() &&
    profitEstimates.yearly.isZero() &&
    lendItem.interestRate.gt(0)
      ? '~'
      : ''

  return (
    <div className="form">
      <ButtonClose onClick={vm.lendDialog.hide} right={24} top={8} />

      <div className="lend-form__header">
        <div className="txt-center">
          <Icon name={spriteLogo} size="auto" className="form__info_block__logo_svg" />
        </div>
        <div>
          <div className="txt-title txt-default">
            {lendForm.lendType} {lendForm.inputLendAsset}
          </div>
          <div className="lend-form__stats">
            <div className="txt-alternate txt-muted">
              APR{' '}
              <InfoTip id="lend-apr-tooltip" top={-1}>
                <p>
                  Lend <b>100</b> {lendItem.asset} and you will have{' '}
                  <b>{autoFormat((nextSupplyRate || lendItem.interestRate).plus(100))}</b>{' '}
                  {lendItem.asset} after a year at current rate.
                </p>
                <p>Rates always change depending on supply and demand.</p>
              </InfoTip>
              <div className="txt-default txt-subtitle">
                {numPrefix}
                <span title={lendItem.interestRate.toFixed()}>
                  {formatPercent(lendItem.interestRate)}%
                </span>{' '}
                {nextSupplyRate && (
                  <span className="txt-positive" title={nextSupplyRate.toFixed()}>
                    → {formatPercent(nextSupplyRate)}%
                  </span>
                )}
              </div>
            </div>

            {!isLending && (
              <div className="txt-alternate txt-muted">
                Avl. Liquidity{' '}
                <InfoTip id="lend-liquidity-tooltip" top={-1}>
                  Available liquidity is the maximum amount of {lendItem.asset} that can be unlent
                  at the moment.
                </InfoTip>
                <div className="txt-default txt-subtitle" title={lendForm.freeSupply.toFixed()}>
                  <span className={lendForm.insufficientLiquidityToUnlend ? 'txt-negative' : ''}>
                    {abbreviate(lendForm.freeSupply)} {lendForm.inputLendAsset}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="form__content">
        {!lendForm.wrapNativeFormVisible && (
          <form onSubmit={lendForm.submit}>
            <div className="margin-bottom-2">
              <InputAmount
                isLoading={lendForm.isLoading}
                inputAmount={lendForm.inputLendAmount}
                selectedAsset={lendForm.inputLendAsset}
                selectorAssets={lendForm.selectableAssets}
                buttonValue={lendForm.ratioMaxLendAmount}
                onInsertMaxValue={lendForm.setAmountFromMaxPercent}
                onAmountChange={lendForm.changeAmount}
                onCollateralChange={lendForm.changeCollateral}
                isGrouped={lendForm.selectableAssets.length === 1}
                withSlider>
                <div className="input-amount__row-container">
                  <div className="txt-subtitle">{isLending ? 'Lend' : 'Unlend'} amount</div>
                  <div
                    className="txt-subtitle txt-default"
                    title={lendForm.maxLendAmount.toFixed()}>
                    <Button variant="link" onClick={lendForm.setMax}>
                      {autoFormat(lendForm.maxLendAmount)}
                    </Button>{' '}
                    <span className="margin-right-1ch">{lendForm.inputLendAsset}</span>
                    <Icon name="wallet-balance" color="var(--color-txt-muted)" size={20} top={-2} />
                  </div>
                </div>
              </InputAmount>
            </div>

            <div className="lend-form__profit-grid margin-bottom-1">
              <div className="panel--simple padded-1 overflow-hidden">
                <div className="txt-subtitle no-wrap">Weekly Profit</div>
                <div className="txt-positive">
                  {numPrefix}
                  {autoFormat(profitEstimates.weekly)}
                </div>
              </div>
              <div className="panel--simple padded-1 overflow-hidden lend-form__hide_on_small">
                <div className="txt-subtitle no-wrap">Monthly Profit</div>
                <div className="txt-positive">
                  {numPrefix}
                  {autoFormat(profitEstimates.monthly)}
                </div>
              </div>
              <div className="panel--simple padded-1 overflow-hidden">
                <div className="txt-subtitle no-wrap">Yearly Profit</div>
                <div className="txt-positive">
                  {numPrefix}
                  {autoFormat(profitEstimates.yearly)}
                </div>
              </div>
            </div>

            <div className="margin-bottom-2">
              You will receive {`${isLending ? 'i' : ''}${displayName}`}{' '}
              {isLending && (
                <InfoTip id="tip-what-are-itokens" top={-1}>
                  <p>
                    iTokens (interest-earning tokens) are minted when you deposit corresponding
                    assets. iTokens increase in value and are worth more than the underlying asset.
                  </p>
                  <ExternalLink href={links.articles.itokens} className="link">
                    (Learn More)
                  </ExternalLink>
                </InfoTip>
              )}
            </div>

            {lendForm.warningMessage ? (
              <p className="form__warning">{lendForm.warningMessage}</p>
            ) : lendForm.needsApproval ? (
              <Button
                variant="green"
                className="block"
                isLoading={lendForm.isLoading || lendForm.spendingApprovalPending}
                onClick={lendForm.approveSpending}>
                Approve spending
              </Button>
            ) : lendForm.isExpired ? (
              <Button variant="green" onClick={noop}>
                Update Price
              </Button>
            ) : (
              <Button
                className="block"
                variant={isLending ? 'green' : 'red'}
                disabled={!lendForm.canSubmit}
                isLoading={lendForm.isLoading}
                type="submit">
                {lendForm.lendType} {autoFormat(lendForm.inputLendAmount)} {displayName}
              </Button>
            )}
          </form>
        )}
        {lendForm.wrapNativeFormVisible && (
          <>
            {lendForm.wrapNativeForm.useWrappedNative && (
              <div className="txt-left margin-bottom-2">
                <Button
                  variant="link"
                  onClick={lendForm.wrapNativeForm.useWrappedNative}
                  onClickEmit="value"
                  value={chain.native.wrapped}>
                  Back to use {chain.native.wrapped} <Icon name="caret-right" size={16} top={-2} />
                </Button>
              </div>
            )}
            <h4 className="h4--muted">
              Only the wrapped token, {chain.native.wrapped}, is supported on{' '}
              {chain.chainDetails.shortName}.
            </h4>
            {isLending && <WrapNativeForm form={lendForm.wrapNativeForm} />}
            {!isLending && (
              <>
                <p>If you would like to get back the native ETH.</p>
                <ol className="txt-explanation list-spaced-2">
                  <li>
                    <Button variant="link" onClick={lendForm.useWrappedNative}>
                      Unlend {chain.native.wrapped}
                    </Button>
                    .
                  </li>
                  <li>
                    Then use our{' '}
                    <Link className="link--discreet" to="/tools/wrap-native">
                      native token swap tool
                    </Link>{' '}
                    to get {chain.native.asset}.
                  </li>
                </ol>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default observer(LendForm)
